"use client";

import { IconAlertInfoOutline18, IconCheckOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { useColorMode } from "@chakra-ui/react";
import { highlight, languages } from "prismjs";
import "prismjs/components/prism-json";
import { useMemo } from "react";
import Editor from "react-simple-code-editor";

interface JsonEditorBaseProps {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  validation?: {
    valid: boolean;
    error?: string;
  };
  showValidationInline?: boolean; // Show validation next to label
  customValidate?: (jsonString: string) => { valid: boolean; error?: string };
  hideClear?: boolean; // Hide the built-in Clear button (when parent handles it)
}

/**
 * Shared JSON editor component with syntax highlighting and validation
 * Used by FilterInput, JsonInput, and other JSON-based inputs
 */
export default function JsonEditorBase({
  label,
  description,
  value,
  onChange,
  placeholder = "{}",
  minHeight = "200px",
  validation,
  showValidationInline = false,
  customValidate,
  hideClear = false,
}: JsonEditorBaseProps) {
  const { colorMode } = useColorMode();

  // Use custom validation if provided, otherwise validate JSON syntax
  const validationResult = useMemo(() => {
    if (validation) return validation;

    if (!value || value.trim() === "") {
      return { valid: true };
    }

    if (customValidate) {
      return customValidate(value);
    }

    // Default: basic JSON validation
    try {
      JSON.parse(value);
      return { valid: true };
    } catch (e) {
      return {
        valid: false,
        error: e instanceof Error ? e.message : "Invalid JSON",
      };
    }
  }, [value, validation, customValidate]);

  // Check if JSON is already formatted
  const isFormatted = useMemo(() => {
    if (!value || !validationResult.valid) return true;
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      return value.trim() === formatted.trim();
    } catch {
      return true;
    }
  }, [value, validationResult.valid]);

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(value || "{}");
      const formatted = JSON.stringify(parsed, null, 2);
      onChange(formatted);
    } catch {
      // Invalid JSON, can't format
    }
  };

  return (
    <div className="space-y-2">
      {(label ||
        showValidationInline ||
        !isFormatted ||
        (value && value.trim() !== "" && value.trim() !== "{}" && value.trim() !== "[]")) && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {label && <label className="field-label block">{label}</label>}

            {/* Inline validation indicator */}
            {showValidationInline && value && (
              <>
                {validationResult.valid ? (
                  <div className="flex items-center gap-1.5">
                    <IconCheckOutline18 className="h-3.5 w-3.5 text-green-600" />
                    <p className="text-xs font-medium uppercase tracking-wider text-green-600 dark:text-green-400">
                      Valid
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <IconXmarkOutline18 className="h-3.5 w-3.5 text-red-500" />
                    <p className="text-xs font-medium uppercase tracking-wider text-red-600 dark:text-red-400">
                      Invalid
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Format button - only show if not already formatted */}
            {!isFormatted && (
              <button
                type="button"
                onClick={handleFormat}
                className="label label-xs text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Format
              </button>
            )}

            {/* Clear button - show when value has meaningful content */}
            {!hideClear && value && value.trim() !== "" && value.trim() !== "{}" && value.trim() !== "[]" && (
              <button
                type="button"
                onClick={() => onChange(value.trimStart().startsWith("[") ? "[]" : "{}")}
                className="label label-xs text-red-500 transition-colors hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      <div className="relative">
        <div
          className={`dark:bg-ds-neutral-800 rounded-lg border bg-white focus-within:outline-none focus-within:ring-2 ${
            validationResult.valid
              ? "border-ds-neutral-300 dark:border-ds-neutral-700 focus-within:border-blue-500 focus-within:ring-blue-500/20"
              : "border-red-500 focus-within:border-red-500 focus-within:ring-red-500/20 dark:border-red-500"
          }`}
        >
          <Editor
            value={value || ""}
            onValueChange={onChange}
            highlight={code => {
              const html = highlight(code, languages.json, "json");
              // Highlight template variables {{...}} in blue pill style.
              // Safe: Prism HTML-encodes user input before we run this regex,
              // so $1 cannot contain unescaped HTML. However, this relies on
              // Prism not splitting `{{` across multiple <span> tokens — if
              // that ever happens the regex will silently miss the variable.
              return html.replace(/\{\{([^}]+)\}\}/g, '<span class="json-variable-highlight">{{$1}}</span>');
            }}
            placeholder={placeholder}
            padding={12}
            style={{
              fontFamily: '"Fira Code", "Fira Mono", Consolas, Monaco, "Courier New", monospace',
              fontSize: 14,
              lineHeight: 1.6,
              minHeight,
              outline: "none",
            }}
            preClassName="text-ds-text-primary dark:text-zinc-200"
            textareaClassName="caret-zinc-900 dark:caret-white"
            className={colorMode === "dark" ? "prism-dark-theme" : "prism-light-theme"}
          />
        </div>

        {/* Unified Prism color scheme */}
        <style jsx global>{`
          /* Light theme colors - unified with WorkflowJsonView */
          .prism-light-theme {
            color: #18181b;
          }
          .prism-light-theme .token.comment,
          .prism-light-theme .token.prolog,
          .prism-light-theme .token.doctype,
          .prism-light-theme .token.cdata {
            color: #a1a1aa;
          }
          .prism-light-theme .token.punctuation {
            color: #71717a;
          }
          .prism-light-theme .token.property,
          .prism-light-theme .token.tag,
          .prism-light-theme .token.symbol {
            color: #a855f7;
          }
          .prism-light-theme .token.boolean,
          .prism-light-theme .token.number,
          .prism-light-theme .token.constant {
            color: #ea580c;
          }
          .prism-light-theme .token.deleted {
            color: #dc2626;
          }
          .prism-light-theme .token.selector,
          .prism-light-theme .token.string,
          .prism-light-theme .token.char,
          .prism-light-theme .token.builtin,
          .prism-light-theme .token.inserted {
            color: #059669;
          }
          .prism-light-theme .token.attr-name,
          .prism-light-theme .token.function,
          .prism-light-theme .token.class-name {
            color: #d97706;
          }
          .prism-light-theme .token.operator,
          .prism-light-theme .token.entity,
          .prism-light-theme .token.url,
          .prism-light-theme .token.atrule,
          .prism-light-theme .token.keyword {
            color: #2563eb;
          }
          .prism-light-theme .token.attr-value {
            color: #059669;
          }

          /* Dark theme colors - unified with WorkflowJsonView */
          .prism-dark-theme {
            color: #e4e4e7;
          }
          .prism-dark-theme .token.comment,
          .prism-dark-theme .token.prolog,
          .prism-dark-theme .token.doctype,
          .prism-dark-theme .token.cdata {
            color: #71717a;
          }
          .prism-dark-theme .token.punctuation {
            color: #a1a1aa;
          }
          .prism-dark-theme .token.property,
          .prism-dark-theme .token.tag,
          .prism-dark-theme .token.symbol {
            color: #e879f9;
          }
          .prism-dark-theme .token.boolean,
          .prism-dark-theme .token.number,
          .prism-dark-theme .token.constant {
            color: #fb923c;
          }
          .prism-dark-theme .token.deleted {
            color: #ef4444;
          }
          .prism-dark-theme .token.selector,
          .prism-dark-theme .token.string,
          .prism-dark-theme .token.char,
          .prism-dark-theme .token.builtin,
          .prism-dark-theme .token.inserted {
            color: #34d399;
          }
          .prism-dark-theme .token.attr-name,
          .prism-dark-theme .token.function,
          .prism-dark-theme .token.class-name {
            color: #fbbf24;
          }
          .prism-dark-theme .token.operator,
          .prism-dark-theme .token.entity,
          .prism-dark-theme .token.url,
          .prism-dark-theme .token.atrule,
          .prism-dark-theme .token.keyword {
            color: #60a5fa;
          }
          .prism-dark-theme .token.attr-value {
            color: #34d399;
          }

          /* Template variable highlighting {{...}} */
          .prism-light-theme .json-variable-highlight {
            color: #2563eb;
            background: #dbeafe;
            border-radius: 3px;
            padding: 1px 3px;
            box-shadow: 0 0 0 1px #bfdbfe;
          }
          .prism-dark-theme .json-variable-highlight {
            color: #60a5fa;
            background: rgba(37, 99, 235, 0.2);
            border-radius: 3px;
            padding: 1px 3px;
          }

          /* Force text wrapping inside react-simple-code-editor */
          .prism-light-theme textarea,
          .prism-light-theme pre,
          .prism-dark-theme textarea,
          .prism-dark-theme pre {
            white-space: pre-wrap !important;
            word-break: break-all !important;
          }
        `}</style>
      </div>
      {/* Error message below editor (if not showing inline) */}
      {!showValidationInline && !validationResult.valid && validationResult.error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-900 dark:bg-red-950/20">
          <IconAlertInfoOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
          <p className="text-xs text-red-700 dark:text-red-300">{validationResult.error}</p>
        </div>
      )}
    </div>
  );
}
